import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { callFunction } from '@/utils/supabaseFunctions';
import { formatCurrency } from '@/lib/format';
import { format } from 'date-fns';
import { Payment } from './payment/types';

interface Organization {
  name: string;
  contact_email?: string;
  contact_phone?: string;
  logo_url?: string;
}

interface Family {
  name: string;
  primary_parent_email?: string;
}

interface ReceiptEmailData {
  payment: Payment;
  organization: Organization;
  family: Family;
  invoice?: {
    id: string;
    total_cents: number;
    due_date: string;
  };
}

const PAYMENT_METHOD_LABELS = {
  stripe: 'Credit Card',
  cash: 'Cash',
  check: 'Check',
  money_order: 'Money Order',
  manual_card: 'Manual Card',
};

export const ReceiptEmailService = {
  /**
   * Send payment receipt email to family
   */
  async sendPaymentReceipt(data: ReceiptEmailData): Promise<boolean> {
    const context: ErrorContext = {
      operation: 'sendPaymentReceipt',
      additionalInfo: {
        paymentId: data.payment.id,
        organizationId: data.payment.organization_id,
        familyId: data.payment.family_id,
      },
    };

    try {
      if (!data.family.primary_parent_email) {
        console.warn('No primary parent email found for family, skipping receipt email');
        return false;
      }

      const emailData = {
        to: data.family.primary_parent_email,
        subject: `Payment Receipt #${data.payment.receipt_number} - ${data.organization.name}`,
        html: ReceiptEmailService.generateReceiptHTML(data),
        from: data.organization.contact_email || 'noreply@hivetivity.com',
        organization_name: data.organization.name,
      };

      // Use the same edge function pattern as invoice emails
      const sendResult = await callFunction('send-parent-invite', {
        method: 'POST',
        body: {
          email: emailData.to,
          subject: emailData.subject,
          html: emailData.html,
        }
      });
      
      console.log('Receipt email sent via edge function:', sendResult);
      return true;
    } catch (error) {
      handleError(error, 'Failed to send payment receipt email', ErrorSeverity.WARNING, context);
      return false;
    }
  },

  /**
   * Generate HTML email template for payment receipt
   */
  generateReceiptHTML(data: ReceiptEmailData): string {
    const { payment, organization, family, invoice } = data;
    
    const getPaymentMethodDisplay = () => {
      const label = PAYMENT_METHOD_LABELS[payment.payment_method];
      if (payment.payment_method === 'check' && payment.check_number) {
        return `${label} #${payment.check_number}`;
      }
      return label;
    };


    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Payment Receipt</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #374151;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
            text-align: center;
          }
          .logo {
            width: 60px;
            height: 60px;
            background-color: #2563eb;
            border-radius: 8px;
            margin: 0 auto 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 24px;
            font-weight: bold;
          }
          .organization-name {
            font-size: 24px;
            font-weight: bold;
            color: #111827;
            margin-bottom: 10px;
          }
          .receipt-title {
            font-size: 32px;
            font-weight: bold;
            color: #2563eb;
            margin: 20px 0;
          }
          .receipt-info {
            background-color: #f9fafb;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 25px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
          }
          .info-label {
            color: #6b7280;
            font-weight: 500;
          }
          .info-value {
            font-weight: 600;
          }
          .amount-section {
            background-color: #ecfdf5;
            border: 2px solid #10b981;
            border-radius: 8px;
            padding: 20px;
            text-align: center;
            margin: 25px 0;
          }
          .amount {
            font-size: 36px;
            font-weight: bold;
            color: #059669;
          }
          .notes-section {
            background-color: #eff6ff;
            padding: 15px;
            border-radius: 8px;
            margin: 20px 0;
          }
          .footer {
            border-top: 1px solid #e5e7eb;
            padding-top: 20px;
            margin-top: 30px;
            text-align: center;
            color: #6b7280;
            font-size: 14px;
          }
          .status-badge {
            background-color: #dcfce7;
            color: #166534;
            padding: 4px 12px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
          }
          @media (max-width: 600px) {
            .info-row {
              flex-direction: column;
              gap: 5px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${organization.logo_url ? 
            `<img src="${organization.logo_url}" alt="${organization.name}" style="width: 60px; height: 60px; object-fit: contain; margin: 0 auto 15px; display: block;">` :
            `<div class="logo">🧾</div>`
          }
          <div class="organization-name">${organization.name}</div>
          <div class="receipt-title">PAYMENT RECEIPT</div>
        </div>

        <div class="receipt-info">
          <div class="info-row">
            <span class="info-label">Receipt Number:</span>
            <span class="info-value">${payment.receipt_number}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Date:</span>
            <span class="info-value">${format(new Date(payment.paid_at || payment.created_at), 'MMMM d, yyyy')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Method:</span>
            <span class="info-value">${getPaymentMethodDisplay()}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="status-badge">${payment.status}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Family:</span>
            <span class="info-value">${family.name}</span>
          </div>
        </div>

        <div class="amount-section">
          <div style="font-size: 18px; margin-bottom: 10px; color: #059669;">Payment Amount</div>
          <div class="amount">${formatCurrency((payment.amount_cents || 0) / 100)}</div>
          ${invoice ? `<div style="font-size: 14px; margin-top: 10px; color: #6b7280;">Applied to Invoice (Due: ${format(new Date(invoice.due_date), 'MMM d, yyyy')})</div>` : ''}
        </div>

        ${payment.manual_notes ? `
          <div class="notes-section">
            <div style="font-weight: 600; margin-bottom: 8px; color: #1f2937;">Notes:</div>
            <div>${payment.manual_notes}</div>
          </div>
        ` : ''}

        <div style="text-align: center; margin: 30px 0;">
          <div style="font-size: 18px; color: #059669; font-weight: 600; margin-bottom: 10px;">
            ✅ Payment Received Successfully
          </div>
          <div style="color: #6b7280;">
            Thank you for your payment! This receipt confirms your transaction.
          </div>
        </div>

        <div class="footer">
          <div>This receipt was generated automatically by ${organization.name}</div>
          <div style="margin-top: 5px;">Generated on ${format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}</div>
          ${organization.contact_phone || organization.contact_email ? `
            <div style="margin-top: 15px;">
              ${organization.contact_phone ? `📞 ${organization.contact_phone}` : ''} 
              ${organization.contact_phone && organization.contact_email ? ' • ' : ''}
              ${organization.contact_email ? `📧 ${organization.contact_email}` : ''}
            </div>
          ` : ''}
        </div>
      </body>
      </html>
    `;
  },
};